import React, {useState, useMemo, useEffect, useCallback} from "react"
import {Paper, Box, useTheme} from "@mui/material"
import {useNavigate} from "react-router-dom"
import {ReactComponent as CheckIcon} from "src/v2/assets/icons/check-icon.svg"
import {ReactComponent as EditIcon} from "src/v2/assets/icons/lock-empty-icon.svg"
import FormActions from "../../components/FormActions"
import {dispatchSnackbar} from "../../../../components/SnackBar/snackbar-bus"
import MotorEventosOrdinariosService from "src/services/motor-eventos-ordinarios-service"
import EventosOrdinariosTable from "./components/eventos-ordinarios/eventos-ordinarios-table"
import FormSectionAccordion from "../../../../components/FormSectionAccordion"
import DataAniversarioTable from "./components/data-aniversario/data-aniversario-table"
import {
    mapEventosOrdinariosPayload,
    mapEventosOrdinariosToTable
} from "./components/eventos-ordinarios/map-eventos-ordinarios"
import MotorDataAniversarioService from "src/services/motor-data-aniversario-service"
import PremioTable from "./components/premio/premio-table"
import {mapPremioPayload} from "./components/premio/premio-fields"
import MotorPremioService from "../../../../../services/motor-premio-service"
import {mapDataAniversarioPayload, mapDataAniversarioToTable} from "./components/data-aniversario/data-aniversario-fields"
import RecompraTable from "./components/recompra-programada/recompra-programada-table"
import {
    mapRecompraProgramadaPayload,
    mapRecompraProgramadaToTable
} from "./components/recompra-programada/recompra-programada-fields"
import ConversabilidadeTable from "./components/conversibilidade/conversabilidade-table"
import {mapConversibilidadePayload} from "./components/conversibilidade/conversabilidade-fields"
import MotorRecompraProgramadaService from "../../../../../services/motor-recompra-programada-service"
import MotorConversibilidadeService from "../../../../../services/motor-conversibilidade-service"
import DuplaConfirmacao from "../../../../components/DuplaConfirmacao/DuplaConfirmacao"
import PropTypes from "prop-types"

import {sortTableItemsByDate} from "../componentes/chronological-table-utils"

const TABLE_DATA_DEFAULT = [{}]
const INDEXADORES_DATA_ANIVERSARIO = [
    "INCC",
    "TR",
    "IPC_FIPE",
    "IPCA",
    "INPC",
    "INCC_DI",
    "IGPDI",
    "IGPM"
]

const EventosOrdinariosPage = (
    {
        idCaracteristica,
        producao,
        setProducao,
        loading,
        jurosOptions,
        setLoading,
        setSourceLoading,
        eventosProjetados,
        datasAniversarioProjetadas,
        recompraProgramadaProjetados,
        onDraftChange,
        onDraftEdit,
    }) => {
    const eventosService = useMemo(() => new MotorEventosOrdinariosService(), [])
    const dataAniversarioService = useMemo(() => new MotorDataAniversarioService(), [])
    const premioService = useMemo(() => new MotorPremioService(), [])
    const recompraProgramadaService = useMemo(() => new MotorRecompraProgramadaService(), [])
    const conversibilidadeService = useMemo(() => new MotorConversibilidadeService(), [])

    const [dataAniversarioPublicado, setDataAniversarioPublicado] = useState(false)
    const [premioPublicado, setPremioPublicado] = useState(false)
    const [recompraPublicado, setRecompraPublicado] = useState(false)
    const [conversibilidadePublicado, setConversibilidadePublicado] = useState(false)

    const theme = useTheme()
    const navigate = useNavigate()
    const [tableEventosData, setRawTableEventosData] = useState(TABLE_DATA_DEFAULT)
    const [tableAniversarioData, setTableAniversarioData] = useState(TABLE_DATA_DEFAULT)
    const [tablePremioData, setTablePremioData] = useState(TABLE_DATA_DEFAULT)
    const [tableRecompraProgramadaData, setRawTableRecompraProgramadaData] = useState(TABLE_DATA_DEFAULT)
    const [tableConversibilidadeData, setTableConversibilidadeData] = useState(TABLE_DATA_DEFAULT)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [publishConfirmOpen, setPublishConfirmOpen] = useState(false)
    const [showDataAniversario, setShowDataAniversario] = useState(false)
    const [editarOpen, setEditarOpen] = useState(false)
    const [pendingEventosData, setPendingEventosData] = useState(null)

    const setTableEventosData = useCallback((update) => {
        setRawTableEventosData(prev => sortTableItemsByDate(
            typeof update === "function" ? update(prev) : update,
            {dateKey: "DataEvento", idKey: "IDFluxoEvento"}
        ))
    }, [])

    const setTableRecompraProgramadaData = useCallback((update) => {
        setRawTableRecompraProgramadaData(prev => sortTableItemsByDate(
            typeof update === "function" ? update(prev) : update,
            {dateKey: "DataRecompraProgramada", idKey: "IDRecompraProgramada"}
        ))
    }, [])

    useEffect(() => {
        if (!showDataAniversario) setSourceLoading("aniversario", false)
    }, [setSourceLoading, showDataAniversario])

    useEffect(() => {
        onDraftChange?.({
            eventosOrdinarios: tableEventosData,
            datasAniversario: tableAniversarioData,
            recomprasProgramadas: tableRecompraProgramadaData,
        })
    }, [
        onDraftChange,
        tableAniversarioData,
        tableEventosData,
        tableRecompraProgramadaData,
    ])

    const handleEditarClick = (e) => {
        e?.preventDefault()
        e?.stopPropagation()
        setEditarOpen(true)
    }

    const handleConfirmEditar = () => {
        setEditarOpen(false)
        handlePublish()
    }

    const handleCancelEditar = () => {
        setEditarOpen(false)
    }

    const getEventosPayload = () => {
        try {
            return mapEventosOrdinariosPayload(tableEventosData)
        } catch (error) {
            dispatchSnackbar(error.message, "error")
            return null
        }
    }

    const handleSave = async () => {
        const eventoOrdinario = getEventosPayload()
        if (!eventoOrdinario) return
        setLoading(true)

        try {
            const dataAniversario = mapDataAniversarioPayload(tableAniversarioData, idCaracteristica)
            const premio = mapPremioPayload(tablePremioData, idCaracteristica)
            const recompraProgramada = mapRecompraProgramadaPayload(tableRecompraProgramadaData, idCaracteristica)
            const conversibilidade = mapConversibilidadePayload(tableConversibilidadeData, idCaracteristica)

            const requests = [
                {
                    key: "eventosOrdinarios",
                    field: "EventosOrdinarios",
                    label: "Eventos Ordinários",
                    payload: eventoOrdinario,
                    promise: () => eventosService.createCadastroTemporarioEventosOrdinarios(idCaracteristica, eventoOrdinario),
                },
                {
                    key: "dataAniversario",
                    field: "DatasAniversario",
                    label: "Data Aniversário",
                    payload: dataAniversario,
                    promise: () => dataAniversarioService.createCadastroTemporarioDataAniversario(idCaracteristica, dataAniversario),
                },
                {
                    key: "premio",
                    field: "Premios",
                    label: "Prêmio",
                    payload: premio,
                    promise: () => premioService.createCadastroTemporarioPremio(idCaracteristica, premio),
                },
                {
                    key: "recompraProgramada",
                    field: "RecomprasProgramadas",
                    label: "Recompra Programada",
                    payload: recompraProgramada,
                    promise: () => recompraProgramadaService.createCadastroTemporarioRecompraProgramada(idCaracteristica, recompraProgramada),
                },
                {
                    key: "conversibilidade",
                    field: "Conversibilidades",
                    label: "Conversibilidade",
                    payload: conversibilidade,
                    promise: () => conversibilidadeService.createCadastroTemporarioConversibilidade(idCaracteristica, conversibilidade),
                },
            ]

            const validRequests = requests.filter((item) => {
                const arrayField = item.payload?.[item.field]
                return Array.isArray(arrayField) && arrayField.length > 0
            })

            const results = await Promise.allSettled(
                validRequests.map((item) => item.promise())
            )

            const responseSummary = validRequests.map((request, index) => {
                const result = results[index]

                if (result.status === "fulfilled") {
                    return {
                        key: request.key,
                        label: request.label,
                        success: true,
                        data: result.value?.body ?? result.value ?? null,
                        error: null,
                    }
                }

                return {
                    key: request.key,
                    label: request.label,
                    success: false,
                    data:
                        result.reason?.response?.text ??
                        `Erro ao salvar ${request.label}`,
                    error:
                        result.reason?.response?.data?.erro ||
                        result.reason?.response?.data?.error ||
                        result.reason?.message ||
                        "Erro desconhecido",
                }
            })

            const errors = responseSummary.filter((item) => !item.success)
            if (errors.length > 0) {
                errors.forEach((item) => {
                    dispatchSnackbar(item.data || `Erro ao salvar ${item.label}`, "error")
                })
                return
            }

            dispatchSnackbar("Todos os dados foram salvos com sucesso!", "success")
        } catch (error) {
            dispatchSnackbar(error.message, "error")
        } finally {
            setLoading(false)
        }
    }

    const buildRequest = ({key, label, payload, published, createFn, updateFn}) => ({
        key,
        label,
        payload,
        promise: published ? updateFn : createFn,
    })

    const buildRequestDelete = ({key, label, published, deleteTemp, deleteProd}) => ({
        key,
        label,
        promise: published ? deleteProd : deleteTemp,
    })

    const handlePublish = async () => {
        const eventoOrdinario = getEventosPayload()
        if (!eventoOrdinario) {
            setPublishConfirmOpen(false)
            return
        }
        try {
            setLoading(true)
            const {EventosOrdinarios} = eventoOrdinario
            const {DatasAniversario} = mapDataAniversarioPayload(tableAniversarioData, idCaracteristica)
            const Premios = mapPremioPayload(tablePremioData, idCaracteristica)
            const RecomprasProgramadas = mapRecompraProgramadaPayload(tableRecompraProgramadaData, idCaracteristica)
            const {Conversibilidades} = mapConversibilidadePayload(tableConversibilidadeData, idCaracteristica)

            const requests = [
                buildRequest({
                    key: "eventosOrdinarios",
                    label: "Eventos Ordinários",
                    field: "EventosOrdinarios",
                    payload: EventosOrdinarios,
                    published: producao,
                    createFn: () => eventosService.createCadastroEventosOrdinarios(idCaracteristica, EventosOrdinarios),
                    updateFn: () => eventosService.updateCadastroEventosOrdinarios(idCaracteristica, EventosOrdinarios),
                }),
                buildRequest({
                    key: "dataAniversario",
                    label: "Data Aniversário",
                    payload: DatasAniversario,
                    published: dataAniversarioPublicado,
                    createFn: () => dataAniversarioService.createCadastroDataAniversario(idCaracteristica, DatasAniversario),
                    updateFn: () => dataAniversarioService.updateCadastroDataAniversario(idCaracteristica, DatasAniversario),
                }),
                buildRequest({
                    key: "premio",
                    label: "Prêmio",
                    payload: Premios.Premios,
                    published: premioPublicado,
                    createFn: () => premioService.createCadastroPremio(idCaracteristica, Premios),
                    updateFn: () => premioService.updateCadastroPremio(idCaracteristica, Premios),
                }),
                buildRequest({
                    key: "recompraProgramada",
                    label: "Recompra Programada",
                    payload: RecomprasProgramadas.RecomprasProgramadas,
                    published: recompraPublicado,
                    createFn: () => recompraProgramadaService.createCadastroRecompraProgramada(idCaracteristica, RecomprasProgramadas),
                    updateFn: () => recompraProgramadaService.updateCadastroRecompraProgramada(idCaracteristica, RecomprasProgramadas),
                }),
                buildRequest({
                    key: "conversibilidade",
                    label: "Conversibilidade",
                    payload: Conversibilidades,
                    published: conversibilidadePublicado,
                    createFn: () => conversibilidadeService.createCadastroConversibilidade(idCaracteristica, Conversibilidades),
                    updateFn: () => conversibilidadeService.updateCadastroConversibilidade(idCaracteristica, Conversibilidades),
                }),
            ]

            const validRequests = requests.filter((item) => item.payload.length > 0)

            const results = await Promise.allSettled(
                validRequests.map((item) => item.promise())
            )

            const responseSummary = validRequests.map((request, index) => {
                const result = results[index]

                if (result.status === "fulfilled") {
                    return {
                        key: request.key,
                        label: request.label,
                        success: true,
                        data: result.value?.body ?? result.value ?? null,
                        error: null,
                    }
                }

                return {
                    key: request.key,
                    label: request.label,
                    success: false,
                    data:
                        result.reason?.response?.text ??
                        `Erro ao salvar ${request.label}`,
                    error:
                        result.reason?.response?.data?.erro ||
                        result.reason?.response?.data?.error ||
                        result.reason?.message ||
                        "Erro desconhecido",
                }
            })

            const errors = responseSummary.filter((item) => !item.success)
            if (errors.length > 0) {
                errors.forEach((item) => {
                    dispatchSnackbar(item.data || `Erro ao salvar ${item.label}`, "error")
                })
                return
            }

            setProducao(true)
            dispatchSnackbar(
                producao
                    ? "Cadastro atualizado em produção com sucesso."
                    : "Cadastro publicado com sucesso.",
                "success"
            )
            setProducao(true)
        } catch (error) {
            dispatchSnackbar(error.message, "error")
        } finally {
            setPublishConfirmOpen(false)
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        setLoading(true)

        try {
            const requests = [
                buildRequestDelete({
                    key: "eventosOrdinarios",
                    label: "Eventos Ordinários",
                    published: producao,
                    deleteTemp: () => eventosService.deleteCadastroTemporarioEventosOrdinarios(idCaracteristica),
                    deleteProd: () => eventosService.deleteCadastroEventosOrdinarios(idCaracteristica),
                }),
                buildRequestDelete({
                    key: "dataAniversario",
                    label: "Data Aniversário",
                    published: producao,
                    deleteTemp: () => dataAniversarioService.deleteCadastroTemporarioDataAniversario(idCaracteristica),
                    deleteProd: () => dataAniversarioService.deleteCadastroDataAniversario(idCaracteristica),
                }),
                buildRequestDelete({
                    key: "premio",
                    label: "Prêmio",
                    published: producao,
                    deleteTemp: () => premioService.deleteCadastroTemporarioPremio(idCaracteristica),
                    deleteProd: () => premioService.deleteCadastroPremio(idCaracteristica),
                }),
                buildRequestDelete({
                    key: "recompraProgramada",
                    label: "Recompra Programada",
                    published: producao,
                    deleteTemp: () => recompraProgramadaService.deleteCadastroTemporarioRecompraProgramada(idCaracteristica),
                    deleteProd: () => recompraProgramadaService.deleteCadastroRecompraProgramada(idCaracteristica),
                }),
                buildRequestDelete({
                    key: "conversibilidade",
                    label: "Conversibilidade",
                    published: producao,
                    deleteTemp: () => conversibilidadeService.deleteCadastroTemporarioConversibilidade(idCaracteristica),
                    deleteProd: () => conversibilidadeService.deleteCadastroConversibilidade(idCaracteristica),
                }),
            ]

            const validRequests = requests.filter((item) => {
                const arrayField = item.payload?.[item.field]
                return Array.isArray(arrayField) && arrayField.length > 0
            })

            const results = await Promise.allSettled(
                validRequests.map((item) => item.promise())
            )

            const responseSummary = validRequests.map((request, index) => {
                const result = results[index]

                if (result.status === "fulfilled") {
                    return {
                        key: request.key,
                        label: request.label,
                        success: true,
                        data: result.value?.body ?? result.value ?? null,
                        error: null,
                    }
                }

                return {
                    key: request.key,
                    label: request.label,
                    success: false,
                    data:
                        result.reason?.response?.text ??
                        `Erro ao salvar ${request.label}`,
                    error:
                        result.reason?.response?.data?.erro ||
                        result.reason?.response?.data?.error ||
                        result.reason?.message ||
                        "Erro desconhecido",
                }
            })

            const errors = responseSummary.filter((item) => !item.success)
            if (errors.length > 0) {
                errors.forEach((item) => {
                    dispatchSnackbar(item.data || `Erro ao salvar ${item.label}`, "error")
                })
                return
            }

            dispatchSnackbar("Todos os dados foram removidos com sucesso!", "success")
        } finally {
            setPublishConfirmOpen(false)
            setLoading(false)
            window.location.reload()
        }
    }

    const clearTable = (setClearFunction) => {
        setClearFunction(TABLE_DATA_DEFAULT)
    }
    const activeDataAniversario = (value) => {
        if (!showDataAniversario && value) {
            dispatchSnackbar("Avaliar aplicabilidade de preenchimento de Data Aniversário", "warning")
        }
        setShowDataAniversario(value)
    }

    const normalizaId = (value) => {
        if (value === null || value === undefined || value === "") {
            return null
        }
        const parsed = Number(value)
        return Number.isNaN(parsed) ? null : parsed
    }

    const setTableEventosItem = (data) => {
        if (!jurosOptions?.length) {
            setPendingEventosData(data)
            return
        }

        const updatedItems = data.map((item, index) => {
            const itemAnterior = tableEventosData[index]
            const formulaIndexador = normalizaId(item.FormulaIndexador)
            const jurosCapitalizacao = normalizaId(item.JurosCapitalizacao)
            const formulaAnterior = normalizaId(itemAnterior?.FormulaIndexador)
            const jurosAnterior = normalizaId(itemAnterior?.JurosCapitalizacao)
            const selectedFormula = jurosOptions.find(option => normalizaId(option.IDJuros) === formulaIndexador)
            const selectedJurosCapitalizacao = jurosOptions.find(option => normalizaId(option.IDJuros) === jurosCapitalizacao)
            const formulaAlterada = formulaIndexador !== formulaAnterior
            const jurosAlterado = jurosCapitalizacao !== jurosAnterior
            const deveAtualizarCalculoDiasIndexador = formulaAlterada || item.CalculoDiasIndexador == null
            const deveAtualizarCalculoDias = jurosAlterado || item.CalculoDias == null

            return {
                ...item,
                FormulaIndexador: formulaIndexador,
                JurosCapitalizacao: jurosCapitalizacao,

                CalculoDiasIndexador: deveAtualizarCalculoDiasIndexador
                    ? selectedFormula?.CalculoDias ?? null
                    : item.CalculoDiasIndexador,

                CalculoDias: deveAtualizarCalculoDias
                    ? selectedJurosCapitalizacao?.CalculoDias ?? null
                    : item.CalculoDias,
            }
        })

        setTableEventosData(updatedItems)
        verificaDataAniversario(updatedItems)
    }

    const verificaDataAniversario = (data) => {
        const indexadorFound = data.some(item => {
            if (item.Indexador !== undefined && item.Indexador !== null) {
                return INDEXADORES_DATA_ANIVERSARIO.includes(item.Indexador.toUpperCase())
            }
        })

        if (indexadorFound) {
            activeDataAniversario(true)
            return
        }

        activeDataAniversario(false)
    }

    useEffect(() => {
        if (eventosProjetados !== null) {
            const eventosProjetadosMapped = mapEventosOrdinariosToTable(eventosProjetados)
            onDraftEdit?.()
            setTableEventosItem(eventosProjetadosMapped)
        }
    }, [eventosProjetados, onDraftEdit])

    
    useEffect(() => {
        if (datasAniversarioProjetadas === null) return

        const datasAniversarioMapped =
            mapDataAniversarioToTable(
                datasAniversarioProjetadas
            )

        onDraftEdit?.()
        setTableAniversarioData(datasAniversarioMapped)

        setShowDataAniversario(
            datasAniversarioProjetadas.length > 0
        )
    }, [datasAniversarioProjetadas, onDraftEdit])

    useEffect(() => {
        if(recompraProgramadaProjetados !== null) {
            const recompraProgramadaMapped = mapRecompraProgramadaToTable(recompraProgramadaProjetados)
            onDraftEdit?.()
            setTableRecompraProgramadaData(recompraProgramadaMapped)
        }
    }, [recompraProgramadaProjetados, onDraftEdit])


    useEffect(() => {
        loadGrupoAtivos()
    }, [])

    useEffect(() => {
        if (!pendingEventosData || !jurosOptions?.length) return
        setTableEventosItem(pendingEventosData)
        setPendingEventosData(null)
    }, [pendingEventosData, jurosOptions])

    const loadGrupoAtivos = async () => {
        console.log("chamar grupo ativos aqui")
    }

    return (
        <>
            <Paper sx={{pt: 3, pb: 2, pl: 4, pr: 4, mb: (showDataAniversario ? 0 : 2)}}>
                <EventosOrdinariosTable
                    idCaracteristica={idCaracteristica}
                    setLoading={(value) => setSourceLoading("eventos", value)}
                    tableData={tableEventosData}
                    jurosOptions={jurosOptions}
                    clearTable={clearTable}
                    eventoPublicado={setProducao}
                    setTableData={setTableEventosItem}
                    onDraftEdit={onDraftEdit}
                />
            </Paper>

            {showDataAniversario && (
                <Box sx={{mt: 2, mb: 2}}>
                    <FormSectionAccordion
                        title="Data Aniversário"
                        defaultOpen={true}
                        disableBorder={true}
                    >
                        <DataAniversarioTable
                            setLoading={(value) => setSourceLoading("aniversario", value)}
                            idCaracteristica={idCaracteristica}
                            dataAniversarioPublicado={setDataAniversarioPublicado}
                            showDataAniversario={showDataAniversario}
                            tableData={tableAniversarioData}
                            setTableData={setTableAniversarioData}
                            clearTable={clearTable}
                            datasAniversarioProjetadas={datasAniversarioProjetadas}
                            onDraftEdit={onDraftEdit}
                        />
                    </FormSectionAccordion>
                </Box>
            )}

            <Box sx={{mb: 2}}>
                <FormSectionAccordion
                    title="Prêmio"
                    defaultOpen={true}
                    disableBorder={true}
                >
                    <PremioTable
                        setLoading={(value) => setSourceLoading("premio", value)}
                        idCaracteristica={idCaracteristica}
                        premioPublicado={setPremioPublicado}
                        tableData={tablePremioData}
                        setTableData={setTablePremioData}
                        clearTable={clearTable}
                    />
                </FormSectionAccordion>
            </Box>

            <Box sx={{mb: 2}}>
                <FormSectionAccordion
                    title="Recompra Programada"
                    defaultOpen={true}
                    disableBorder={true}
                >
                    <RecompraTable
                        setLoading={(value) => setSourceLoading("recompra", value)}
                        idCaracteristica={idCaracteristica}
                        recompraPublicado={setRecompraPublicado}
                        tableData={tableRecompraProgramadaData}
                        setTableData={setTableRecompraProgramadaData}
                        clearTable={clearTable}
                        onDraftEdit={onDraftEdit}
                    />
                </FormSectionAccordion>
            </Box>

            <Box sx={{mb: 8}}>
                <FormSectionAccordion
                    title="Conversibilidade"
                    defaultOpen={true}
                    disableBorder={true}
                >
                    <ConversabilidadeTable
                        setLoading={(value) => setSourceLoading("conversibilidade", value)}
                        idCaracteristica={idCaracteristica}
                        conversibilidadePublicado={setConversibilidadePublicado}
                        tableData={tableConversibilidadeData}
                        setTableData={setTableConversibilidadeData}
                        clearTable={clearTable}
                    />
                </FormSectionAccordion>
            </Box>

            <Box sx={{mx: {xxxs: -3, md: -3, lg: 0}, mt: 4}}>
                <FormActions
                    showBack
                    onBack={() => navigate(-1)}
                    onDelete={() => setDeleteConfirmOpen(true)}
                    deleteOpen={deleteConfirmOpen}
                    setDeleteOpen={setDeleteConfirmOpen}
                    onConfirmDelete={handleDelete}
                    showSave={!producao}
                    onSave={!loading ? handleSave : undefined}
                    onPublish={
                        producao
                            ? handleEditarClick
                            : () => setPublishConfirmOpen(true)
                    }
                    publishOpen={publishConfirmOpen}
                    setPublishOpen={setPublishConfirmOpen}
                    onConfirmPublish={handlePublish}
                    publishLabel={
                        producao
                            ? "EDITAR"
                            : "PUBLICAR"
                    }
                    publishIcon={
                        producao
                            ? <EditIcon/>
                            : <CheckIcon/>
                    }
                    theme={theme}/>
            </Box>

            <DuplaConfirmacao
                open={editarOpen}
                cancel={handleCancelEditar}
                confirm={handleConfirmEditar}
                title="Editar Eventos"
                description="
                        Certifique-se de que todas as alterações foram feitas,
                        pois você está alterando um registro em produção.
                    "
            />
        </>
    )
}

EventosOrdinariosPage.propTypes = {
    idCaracteristica: PropTypes.number,
    caracteristica: PropTypes.object,
    cetip: PropTypes.string,
    setLoading: PropTypes.func,
    setSourceLoading: PropTypes.func,
    loading: PropTypes.bool,
    versaoDocumento: PropTypes.number,
    producao: PropTypes.object,
    setProducao: PropTypes.func,
    jurosOptions: PropTypes.array,
    eventosProjetados: PropTypes.array,
    datasAniversarioProjetadas: PropTypes.array,
    recompraProgramadaProjetados: PropTypes.array,
    onDraftChange: PropTypes.func,
    onDraftEdit: PropTypes.func,
}

export default EventosOrdinariosPage
